/**
 * Transport seam.
 *
 * The storefront never talks to a provider, never holds a bearer token, and
 * never performs collection CRUD. It issues same-origin requests. An
 * authenticated collector carries the `lp_customer_session` HttpOnly cookie
 * that core sets, plus the CSRF header on every mutation; a guest carries
 * neither and identifies itself with an email address in the command body and
 * an opaque continuation token in a header.
 *
 * `Transport` is the single injection point. The synthetic core used for
 * development and unit tests implements exactly this type, so no test path can
 * fall through to the network.
 */

export interface CoreRequest {
  readonly path: string;
  readonly method: "GET" | "POST";
  readonly body?: unknown;
  /** In-memory CSRF token. Required by core for a cookie-authenticated mutation. */
  readonly csrfToken?: string;
  /** Guest continuation capability. Never rendered and never logged. */
  readonly checkoutToken?: string;
  readonly correlationID: string;
  readonly signal?: AbortSignal;
}

export type CoreReply =
  | { readonly kind: "http"; readonly status: number; readonly body: unknown }
  | { readonly kind: "network-error"; readonly detail: string };

export type Transport = (request: CoreRequest) => Promise<CoreReply>;

/**
 * What a caller is allowed to observe. Each variant maps to one of the required
 * interface states in Design Bible 13, so a component cannot render a state the
 * transport did not report.
 *
 * `unavailable` carries the distinction that matters most on this surface:
 * core answers 503 when the commerce capability gate is off, which is a
 * *deliberate configuration state*, not a fault. It is surfaced separately as
 * `disabled` so the storefront can say checkout is switched off rather than
 * implying the site is broken.
 */
export type Outcome<T> =
  | { readonly kind: "ok"; readonly value: T }
  | { readonly kind: "denied"; readonly requirement: string }
  | { readonly kind: "not-found" }
  | { readonly kind: "conflict"; readonly detail: string }
  | { readonly kind: "rejected"; readonly detail: string }
  | { readonly kind: "rate-limited" }
  | { readonly kind: "disabled" }
  | { readonly kind: "offline" }
  | { readonly kind: "unavailable"; readonly status: number }
  | { readonly kind: "malformed"; readonly detail: string };

/** Only these outcomes are worth retrying on a schedule (Design Bible 13.1). */
export function isRetryable(outcome: Outcome<unknown>): boolean {
  return outcome.kind === "offline" || outcome.kind === "unavailable";
}

export function correlationID(random: () => number = Math.random): string {
  const bytes = Array.from({ length: 16 }, () =>
    Math.floor(random() * 256)
      .toString(16)
      .padStart(2, "0")
  );
  return bytes.join("");
}

/**
 * Turns a raw reply into an `Outcome`, applying the public contract validator.
 * A 200 whose body does not match the contract is `malformed`, never `ok` —
 * that is the fail-closed half of `publicContract.ts`.
 */
export function interpret<T>(
  reply: CoreReply,
  parse: (value: unknown) => { readonly ok: true; readonly value: T } | { readonly ok: false; readonly reason: string },
  requirement: string
): Outcome<T> {
  if (reply.kind === "network-error") {
    return { kind: "offline" };
  }
  if (reply.status === 401 || reply.status === 403) {
    return { kind: "denied", requirement };
  }
  if (reply.status === 404) {
    return { kind: "not-found" };
  }
  if (reply.status === 409) {
    return { kind: "conflict", detail: messageFrom(reply.body) };
  }
  if (reply.status === 429) {
    return { kind: "rate-limited" };
  }
  if (reply.status === 503) {
    // Core answers 503 with `{"status":"disabled"}` when the commerce or tax
    // capability gate is off (`ErrCommerceDisabled` / `ErrTaxDisabled`).
    return { kind: "disabled" };
  }
  if (reply.status === 400 || reply.status === 422) {
    return { kind: "rejected", detail: messageFrom(reply.body) };
  }
  if (reply.status < 200 || reply.status > 299) {
    return { kind: "unavailable", status: reply.status };
  }
  const parsed = parse(reply.body);
  return parsed.ok ? { kind: "ok", value: parsed.value } : { kind: "malformed", detail: parsed.reason };
}

/**
 * Core's error bodies are `{ "message": ..., "data": ... }`. Only the message
 * is surfaced, and every component that renders it treats it as untrusted text
 * (Design Bible 11.5).
 */
function messageFrom(body: unknown): string {
  if (typeof body === "object" && body !== null && !Array.isArray(body)) {
    const record = body as Record<string, unknown>;
    for (const key of ["message", "error", "status"]) {
      const value = record[key];
      if (typeof value === "string" && value.length > 0 && value.length <= 500) return value;
    }
  }
  return "The server rejected the request without a stated reason.";
}
